import { forwardRef, useState } from 'react'
import {
  PHONE_COUNTRY_CODE,
  PHONE_NATIONAL_DIGITS,
  nationalPhoneDigits,
  spacedPhone,
} from '@hi/contracts/shared'
import { Input, type InputProps } from './Input.js'

export interface PhoneInputProps
  extends Omit<InputProps, 'type' | 'value' | 'onChange' | 'leading' | 'maxLength'> {
  /** The ten national digits — never the +1, never spaces. */
  value: string
  onValueChange: (digits: string) => void
}

/**
 * A Canadian mobile number: "+1" fixed at the front, ten digits after it, no more.
 *
 * The professional's number was a plain text box with a seven-character minimum, so
 * "416 555 01345" and "call me" both went in (client, 2026-10-01). The phone app's own
 * number field (D4) already worked this way; this is the desk's copy of it, reading
 * digits with the same function the server's rule is written beside
 * (`@hi/contracts/shared` `phone.ts`).
 *
 * Whatever is typed or pasted — brackets, dashes, a leading "+1" or "1" — keeps only
 * its national digits, and an eleventh is never accepted. While the box is in use the
 * digits stay plain (spacing them as you type moves the cursor on some browsers); once
 * it is left, the number reads the way it is said, "416 555 0134".
 *
 * The value is the digits; a screen sends `+1` and them, after `readCanadianPhone`
 * says they make a number.
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value, onValueChange, onFocus, onBlur, ...props },
  ref,
) {
  const [typing, setTyping] = useState(false)
  const digits = nationalPhoneDigits(value).slice(0, PHONE_NATIONAL_DIGITS)
  return (
    <Input
      ref={ref}
      type="tel"
      inputMode="tel"
      autoComplete="tel-national"
      placeholder="416 555 0134"
      {...props}
      leading={
        <span className="text-body font-medium text-ink-2 tabular-nums" aria-hidden>
          {PHONE_COUNTRY_CODE}
        </span>
      }
      className="tabular-nums"
      value={typing ? digits : spacedPhone(digits)}
      onChange={(e) => onValueChange(nationalPhoneDigits(e.target.value).slice(0, PHONE_NATIONAL_DIGITS))}
      onFocus={(e) => {
        setTyping(true)
        onFocus?.(e)
      }}
      onBlur={(e) => {
        setTyping(false)
        onBlur?.(e)
      }}
    />
  )
})
